import React from 'react';
import { ArrowLeft } from '@phosphor-icons/react';
import { formatINR, formatPercent, formatVolume } from '../../utils/formatters';

export function StockHeader({ stock, onBack }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <button
        onClick={onBack}
        className="text-[#94A3B8] hover:text-white transition-colors"
        data-testid="back-button"
      >
        <ArrowLeft size={24} weight="bold" />
      </button>
      <div>
        <div className="flex items-baseline gap-3">
          <h1 className="text-3xl font-heading font-black tracking-tighter text-white">{stock.symbol}</h1>
          <span className="text-sm text-[#94A3B8]">{stock.name}</span>
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#64748B] border border-white/10 px-2 py-0.5">
            {stock.sector}
          </span>
        </div>
      </div>
    </div>
  );
}

export function PriceHeader({ stock }) {
  const isBullish = stock.change >= 0;
  const priceLabelColor = isBullish ? 'text-[#00FF66]' : 'text-[#FF3366]';
  const rangeLeft = ((stock.price - stock.day_range_52w_low)
                   / (stock.day_range_52w_high - stock.day_range_52w_low)) * 100;

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6" data-testid="stock-price-header">
      <div className="bg-[#12141A] border border-white/10 p-4 md:col-span-2">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8] mb-1">Live Price</div>
        <div className="flex items-baseline gap-3">
          <span className="text-4xl font-mono font-semibold text-white">{formatINR(stock.price)}</span>
          <span className={`font-mono text-lg ${priceLabelColor}`}>
            {isBullish ? '▲' : '▼'} {formatPercent(stock.change_percent)}
          </span>
        </div>
        <div className="text-xs text-[#64748B] font-mono mt-1">
          Prev Close: {formatINR(stock.prev_close)} • O {formatINR(stock.open)} • H {formatINR(stock.high)} • L {formatINR(stock.low)}
        </div>
      </div>

      <div className="bg-[#12141A] border border-white/10 p-4">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8] mb-1">52W Range</div>
        <div className="text-sm font-mono text-white mt-2">
          {formatINR(stock.day_range_52w_low)} — {formatINR(stock.day_range_52w_high)}
        </div>
        <div className="mt-2 h-1 bg-white/[0.05] relative">
          <div className="absolute h-full bg-[#2563EB]" style={{ left: `${rangeLeft}%`, width: '4px' }} />
        </div>
      </div>

      <div className="bg-[#12141A] border border-white/10 p-4">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8] mb-1">Fundamentals</div>
        <div className="grid grid-cols-2 gap-2 mt-1">
          <StatCell label="P/E" value={stock.pe_ratio} />
          <StatCell label="Div Yield" value={`${stock.dividend_yield}%`} />
          <StatCell label="Volume" value={formatVolume(stock.volume)} />
          <StatCell label="M-Cap" value={`₹${(stock.market_cap / 1000).toFixed(1)}K Cr`} />
        </div>
      </div>
    </div>
  );
}

function StatCell({ label, value }) {
  return (
    <div>
      <div className="text-xs text-[#64748B]">{label}</div>
      <div className="font-mono text-sm text-white">{value}</div>
    </div>
  );
}
